import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Users, Film, ClipboardList, GitBranch, Camera, MessageSquare, Play } from "lucide-react";
import MarketingLayout from "@/components/MarketingLayout";
import { BRAND } from "@/constants/brand";

export default function Landing() {
  return (
    <MarketingLayout>
      <Hero />
      <Problem />
      <Showcase />
      <FeatureGrid />
      <Pipeline />
      <DirectorMode />
      <FinalCTA />
    </MarketingLayout>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden sf-noise" data-testid="hero-section">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: "url('https://images.pexels.com/photos/10395639/pexels-photo-10395639.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }}
      />
      <div className="absolute inset-0 bg-[#0A0A0A]/80"></div>
      <div className="absolute inset-0 sf-vignette"></div>
      <div className="absolute inset-0 sf-scanlines pointer-events-none"></div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-32 min-h-[85vh] flex flex-col justify-center">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-8" data-testid="hero-eyebrow">
          — The Intelligent Creative Workspace
        </div>
        <h1 className="font-editorial text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl">
          Write the Story.<br />
          <span className="italic text-[#D4AF37]">See</span> the Film.<br />
          Build the Production.
        </h1>
        <p className="mt-8 max-w-xl text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
          An intelligent workspace for screenwriters, directors and producers —
          from first idea to production-ready project. No more juggling five different apps.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/signup" className="bg-[#D4AF37] text-[#0A0A0A] px-7 py-3.5 text-sm font-medium tracking-wide hover:bg-[#F59E0B] transition-colors inline-flex items-center gap-2 group" data-testid="hero-cta-primary">
            Start creating free
            <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link to="/features" className="border border-[#27272A] hover:border-[#A1A1AA] text-[#FFFFF0] px-7 py-3.5 text-sm font-medium tracking-wide transition-colors inline-flex items-center gap-2" data-testid="hero-cta-secondary">
            <Play size={14} /> Watch how it works
          </Link>
        </div>

        <div className="mt-16 flex gap-8 text-xs uppercase tracking-widest text-[#71717A]">
          <div>Writers</div>
          <div className="text-[#27272A]">/</div>
          <div>Directors</div>
          <div className="text-[#27272A]">/</div>
          <div>Producers</div>
          <div className="text-[#27272A]">/</div>
          <div>Showrunners</div>
        </div>
      </div>

      {/* Interactive preview mock */}
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
        <div className="border border-[#27272A] bg-[#0F0F10] shadow-2xl" data-testid="hero-preview">
          <div className="border-b border-[#1E1E1E] px-4 py-2.5 flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-[#7F1D1D]"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-[#D4AF37]"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-[#14532D]"></div>
            <span className="ml-3 text-xs text-[#71717A]">the-last-signal.storyframe.ai / Act I / Scene 3</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_280px] min-h-[420px]">
            <div className="border-r border-[#1E1E1E] p-4 text-xs space-y-3 hidden lg:block">
              <div className="uppercase tracking-widest text-[#71717A] mb-3 text-[10px]">Scenes</div>
              {[
                "OBSERVATORY - NIGHT",
                "ROOFTOP - PRE-DAWN",
                "IRIS' APARTMENT",
                "CORRIDOR - LATER",
                "GROUNDS - CONT.",
              ].map((s, i) => (
                <div key={s} className={`py-1.5 px-2 border-l-2 ${i === 2 ? "border-[#D4AF37] bg-[#141414] text-[#FFFFF0]" : "border-transparent text-[#A1A1AA]"}`}>
                  {i + 1}. {s}
                </div>
              ))}
            </div>
            <div className="p-6 font-mono-editor text-[13px] leading-relaxed">
              <div className="uppercase font-bold text-[#FFFFF0]">INT. IRIS&apos; APARTMENT &mdash; DAY</div>
              <div className="mt-3 text-[#E4E4E7]">Small, cluttered, tender. A photo of a boy — nine, gap-toothed — pinned to the fridge.</div>
              <div className="mt-5 pl-[22%] uppercase font-semibold">IRIS</div>
              <div className="pl-[12%] pr-[20%]">Marlow.</div>
              <div className="mt-3 pl-[22%] uppercase font-semibold">VOICE (V.O.)</div>
              <div className="pl-[12%] pr-[20%]">Doctor. We received your report. We&apos;d like you to stop analysing it.</div>
              <div className="mt-4 text-[#71717A] italic border-l-2 border-[#D4AF37] pl-3">// Suggestion: raise the stakes here — Iris should sense a threat, not a request.</div>
            </div>
            <div className="border-l border-[#1E1E1E] p-4 hidden lg:block">
              <div className="uppercase tracking-widest text-[#71717A] mb-3 text-[10px]">AI Assistant</div>
              <div className="space-y-2 text-xs">
                {["Continue scene", "Tighten dialogue", "Increase tension", "Diagnose motivation", "Generate alternative"].map((a) => (
                  <div key={a} className="border border-[#27272A] px-3 py-2 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors cursor-default">
                    {a}
                  </div>
                ))}
              </div>
              <div className="mt-6 text-[10px] tracking-widest uppercase text-[#71717A]">Story Health</div>
              <div className="mt-2 flex items-baseline gap-2">
                <div className="font-editorial text-4xl text-[#D4AF37]">82</div>
                <div className="text-xs text-[#A1A1AA]">/ 100</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Problem() {
  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-6">The Problem</div>
          <h2 className="font-editorial text-4xl sm:text-5xl leading-tight">
            Making a film<br />shouldn&apos;t require<br /><span className="italic text-[#A1A1AA]">five different apps.</span>
          </h2>
        </div>
        <div className="md:col-span-7 grid grid-cols-2 gap-px bg-[#1E1E1E] sf-grid-border">
          {[
            { title: "The script lives in Final Draft", body: "Scene numbers hard-coded. Every rewrite breaks something." },
            { title: "Characters live in Notion", body: "Motivations, arcs, relationships — disconnected from the actual pages." },
            { title: "Storyboards live in Milanote", body: "Beautiful boards nobody updates after week two." },
            { title: "Production lives in a spreadsheet", body: "Cast, locations, budget &mdash; held together by hope." },
          ].map((c) => (
            <div key={c.title} className="bg-[#0A0A0A] p-8">
              <div className="font-editorial text-2xl mb-2">{c.title}</div>
              <div className="text-sm text-[#A1A1AA] leading-relaxed">{c.body}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-16 border-t border-[#1E1E1E] pt-12 text-center">
        <div className="font-editorial text-3xl sm:text-4xl">
          One story. <span className="text-[#D4AF37]">One source of truth.</span> Every stage connected.
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: Sparkles, tag: "AI Writer", title: "Writes like a partner, not a wrapper", body: "Continue a scene, tighten dialogue, increase tension. AI reads your entire project, not just the paragraph.", span: "col-span-8" },
  { icon: ClipboardList, tag: "Script Doctor", title: "Story health, on demand", body: "Structural, character, pacing, dialogue and commercial scores with actionable notes tied to real scenes.", span: "col-span-4" },
  { icon: Users, tag: "Character Lab", title: "Every character, connected", body: "Cues in the script build character entities automatically. Their arcs, goals and dialogue statistics live in one place.", span: "col-span-4" },
  { icon: Film, tag: "Storyboard Studio", title: "From words to frames in one click", body: "Select any scene. Generate a structured shot plan with camera, lighting and imagery.", span: "col-span-8", img: "https://images.pexels.com/photos/23384400/pexels-photo-23384400.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { icon: Camera, tag: "AI Director", title: "A cinematic co-conspirator", body: "Camera, framing, lens, movement, blocking, performance. Directable, editable, apply-able.", span: "col-span-6" },
  { icon: ClipboardList, tag: "Production Hub", title: "Readiness at a glance", body: "One score that tells you how far your project is from being shootable. Backed by everything upstream.", span: "col-span-6" },
];

function Showcase() {
  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 border-t border-[#1E1E1E]">
      <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-6">The Workspace</div>
      <h2 className="font-editorial text-4xl sm:text-5xl leading-tight max-w-3xl mb-14">
        Everything a film needs — in one place that behaves like it knows the difference between a scene heading and a shot list.
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {FEATURES.map((f) => (
          <div key={f.tag}
            className={`bg-[#141414] border border-[#1E1E1E] p-8 hover:border-[#27272A] transition-colors relative overflow-hidden md:${f.span}`}
            data-testid={`feature-${f.tag.toLowerCase().replace(/\s+/g, '-')}`}>
            {f.img && (
              <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: `url(${f.img})` }}></div>
            )}
            <div className="relative">
              <div className="w-9 h-9 border border-[#27272A] flex items-center justify-center mb-5">
                <f.icon size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
              </div>
              <div className="uppercase text-[10px] tracking-widest text-[#71717A] mb-2">{f.tag}</div>
              <div className="font-editorial text-2xl mb-2 max-w-md">{f.title}</div>
              <div className="text-sm text-[#A1A1AA] leading-relaxed max-w-md">{f.body}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FeatureGrid() {
  const items = [
    ["Structural intelligence", "Act 1 / II-A / Midpoint / II-B / Act 3 breakdown with dramatic pressure per scene."],
    ["Character screen-time", "Who speaks, when, how often — visualised as a timeline."],
    ["Relationship graph", "Who loves whom, who betrays whom. Editable, canonical, script-linked."],
    ["Continuity radar", "Wardrobe. Objects. Locations. Names. Flags what your rewrite broke."],
    ["Scene health score", "Dramatic pressure, clarity, pacing, conflict, visual potential, complexity."],
    ["Production cost intelligence", "Night shoots. Crowds. Stunts. VFX. Flagged before the pitch."],
  ];
  return (
    <section className="border-t border-[#1E1E1E] bg-[#0F0F10]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-6">Story Intelligence</div>
        <h2 className="font-editorial text-4xl sm:text-5xl leading-tight max-w-3xl mb-14">
          The system reads your screenplay the way a story editor would — then shows its work.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1E1E1E]">
          {items.map(([t, b], i) => (
            <div key={t} className="bg-[#0A0A0A] p-8">
              <div className="text-xs text-[#71717A] mb-4">— 0{i + 1}</div>
              <div className="font-editorial text-xl mb-2">{t}</div>
              <div className="text-sm text-[#A1A1AA] leading-relaxed">{b}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pipeline() {
  const steps = ["Idea", "Outline", "Draft", "Revision", "Breakdown", "Storyboard", "Casting", "Locations", "Schedule", "Budget", "Production"];
  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 border-t border-[#1E1E1E]">
      <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-6">From Script to Screen</div>
      <h2 className="font-editorial text-4xl sm:text-5xl leading-tight max-w-3xl mb-14">
        One connected pipeline. No exports between apps. No lost data.
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-11 gap-px bg-[#1E1E1E]">
        {steps.map((s, i) => (
          <div key={s} className="bg-[#0A0A0A] p-4 text-center">
            <div className="font-mono-editor text-[10px] text-[#71717A] mb-2">→ {String(i + 1).padStart(2, "0")}</div>
            <div className="font-editorial text-lg">{s}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function DirectorMode() {
  return (
    <section className="border-t border-[#1E1E1E] bg-[#0F0F10] sf-noise">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-6">AI Director Mode</div>
          <h2 className="font-editorial text-4xl sm:text-5xl leading-tight mb-8">
            Not &ldquo;which prompt&rdquo;.<br />
            <span className="italic">Which shot.</span>
          </h2>
          <p className="text-[#A1A1AA] leading-relaxed mb-8 max-w-lg">
            Select a scene. The Director reads its action, dialogue, tone, location and continuity — and proposes a shot plan with framing, lens, movement, lighting and blocking. Every proposal is editable and one click to storyboard.
          </p>
          <ul className="space-y-3 text-sm">
            {["Shot plan with 4-8 shots per scene", "Camera language explained, not just labelled", "Lighting and blocking intent", "Continuity notes across scenes", "One-click apply to storyboard"].map((x) => (
              <li key={x} className="flex items-start gap-3">
                <div className="w-1 h-1 bg-[#D4AF37] mt-2"></div>
                <span className="text-[#E4E4E7]">{x}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="border border-[#27272A] bg-[#0A0A0A]">
          <div className="border-b border-[#1E1E1E] px-4 py-3 text-xs uppercase tracking-widest text-[#71717A]">
            Director Plan &mdash; Scene 3
          </div>
          <div className="p-6 space-y-4 text-sm">
            {[
              ["SHOT 1", "Wide", "Eye level", "Static, 35mm", "Iris framed small in her apartment. World presses in."],
              ["SHOT 2", "Medium OTS", "Low", "Push-in, 50mm", "Over Iris's shoulder as she stares at the phone. Threat lives in the frame."],
              ["SHOT 3", "Close-up", "Eye level", "Handheld, 85mm", "Iris. The realisation. Held one beat too long."],
              ["SHOT 4", "Insert", "High", "Static, 100mm macro", "The boy in the fridge photo. Gap-toothed. Ordinary. Precious."],
            ].map(([n, size, angle, move, desc]) => (
              <div key={n} className="border border-[#1E1E1E] p-4 hover:border-[#D4AF37] transition-colors">
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-[#D4AF37] mb-2">
                  <span>{n}</span>
                  <span className="text-[#71717A]">/</span>
                  <span className="text-[#A1A1AA]">{size}</span>
                  <span className="text-[#71717A]">/</span>
                  <span className="text-[#A1A1AA]">{angle}</span>
                  <span className="text-[#71717A]">/</span>
                  <span className="text-[#A1A1AA]">{move}</span>
                </div>
                <div className="text-[#E4E4E7]">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="border-t border-[#1E1E1E]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 text-center">
        <h2 className="font-editorial text-5xl sm:text-6xl leading-tight max-w-4xl mx-auto">
          Your screenplay is<br />
          <span className="italic text-[#D4AF37]">only the beginning.</span>
        </h2>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/signup" className="bg-[#D4AF37] text-[#0A0A0A] px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#F59E0B] transition-colors" data-testid="final-cta">
            Start your first project
          </Link>
          <Link to="/pricing" className="border border-[#27272A] hover:border-[#A1A1AA] px-8 py-4 text-sm font-medium tracking-wide transition-colors">
            See pricing
          </Link>
        </div>
      </div>
    </section>
  );
}
